import { GutXAxisOrgan, OrganLookupCode } from '@repo/types';
import c from 'classnames';
import { Spinfinity } from '../Spinfinity';
import Dot, { type GutXAxisHoverVariant } from './Dot';
import styles from './GutXAxisBody.module.css';
import Organ from './Organ';
import { useGutXAxisImages } from './hooks';

export * from './systems';

// Vite yields an image import as a URL string; Next yields a StaticImageData
// object. `<img>` needs the string either way.
const toSrc = (image: string): string =>
  typeof image === 'string' ? image : (image as { src: string }).src;

type GutXAxisBodyProps = {
  organs: GutXAxisOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** Omit to render a static body, as the PDF report does. */
  onOrganSelect?: (organ: GutXAxisOrgan) => void;
  /** PDF renders eagerly: paged.js would capture whichever frame it caught. */
  waitForImages?: boolean;
  /** In case we want to hide the tooltips, example: PDFs */
  showDotAndTooltips?: boolean;
  /** Lands on the container. `GutXAxisOrganBadge` uses it to crop this stack. */
  className?: string;
  /** The codes the body draws, where a caller renders only part of the set. */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
  /** `pill` names the organ only, for a surface that lists its flags already. */
  hoverVariant?: GutXAxisHoverVariant;
};

export function GutXAxisBody({
  organs,
  age,
  pregnant,
  sex,
  onOrganSelect,
  waitForImages,
  showDotAndTooltips = true,
  className,
  drawnCodes,
  hoverVariant,
}: GutXAxisBodyProps) {
  const { body, bodyAgeGroup, systems, imgProps, ready } = useGutXAxisImages({
    organs,
    age,
    pregnant,
    sex,
    waitForImages,
    drawnCodes,
  });

  return (
    <div className={c(styles.container, className, !ready && styles.pending)}>
      <img
        src={toSrc(body)}
        className={styles.bodySilhouette}
        {...imgProps('bodySilhouette')}
      />
      {systems.map(({ organ, parts }) => (
        <div key={organ.id} className={styles.system}>
          {parts.map(({ part, src }) => (
            <Organ
              key={part}
              className={c(styles.organ, styles[part], styles[bodyAgeGroup])}
              ariaLabel={organ.display_name}
              onSelect={onOrganSelect ? () => onOrganSelect(organ) : undefined}
            >
              <img
                src={toSrc(src)}
                className={styles.organImage}
                {...imgProps(part)}
              />
              {showDotAndTooltips && (
                <Dot
                  displayName={organ.display_name}
                  flagLevel={organ.flag_level}
                  hoverVariant={hoverVariant}
                />
              )}
            </Organ>
          ))}
        </div>
      ))}
      {!ready && (
        <span className={styles.loader}>
          <Spinfinity />
        </span>
      )}
    </div>
  );
}
